/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState, useEffect, useCallback } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Row, Typography, message } from 'antd';

import { BannerHeaderSearcher } from '@/components/forms/banner-header-searcher.form';
import { BannerHeaderTable } from '@/components/tables/banner-header.table';
import { PermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  deleteBannerHeader,
  getListBannerHeader,
  toggleBannerHeaderPlatform,
  watchListBannerHeader,
  watchBannerHeaderLoading,
  watchBannerHeaderPagination,
  watchBannerHeaderMessage,
  resetMessages
} from '@/redux/slices/banner-header.slice';
import { MESSAGE } from '@/constants/message.constant';
import { Message } from '@/enums/message.enum';

export default function HomeBannerHeader() {
  const [filter, setFilter] = useState<FilterBannerHeader>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { push } = useQuery();
  const dispatch = useAppDispatch();

  const data = useAppSelector(watchListBannerHeader);
  const loading = useAppSelector(watchBannerHeaderLoading);
  const pagination = useAppSelector(watchBannerHeaderPagination);
  const bannerHeaderMessage = useAppSelector(watchBannerHeaderMessage);

  const handlePageChange = useCallback(
    ({
      page = 1,
      pageSize: newPageSize = 10,
      filter: filterParams
    }: {
      page?: number;
      pageSize?: number;
      filter?: FilterBannerHeader;
    }) => {
      const currentFilter = filterParams !== undefined ? filterParams : filter;

      const apiFilter: BannerHeaderListFilter = {};
      if (currentFilter.title) apiFilter.TITLE = currentFilter.title;
      if (currentFilter.displayType)
        apiFilter.DISPLAY_TYPE = currentFilter.displayType;
      if (currentFilter.updatedFrom)
        apiFilter.UPDATE_DATE_START = currentFilter.updatedFrom;
      if (currentFilter.updatedTo)
        apiFilter.UPDATE_DATE_END = currentFilter.updatedTo;

      dispatch(
        getListBannerHeader({
          query: apiFilter,
          offset: page,
          limit: newPageSize
        })
      );
    },
    [dispatch, filter]
  );

  const handleSearch = (values: FilterBannerHeader) => {
    const isReset = Object.keys(values).length === 0;
    const newFilter = isReset ? {} : { ...filter, ...values };
    setFilter(newFilter);
    setCurrentPage(1);
    setPageSize(10);
    handlePageChange({ page: 1, pageSize: 10, filter: newFilter });
  };

  const handleDelete = async (record: BannerHeader) => {
    try {
      await dispatch(deleteBannerHeader(Number(record.id))).unwrap();

      const remainingRecords = data.length - 1;
      if (remainingRecords === 0 && pagination.curPage > 1) {
        handlePageChange({
          page: pagination.curPage - 1,
          pageSize: pagination.limitPage,
          filter
        });
      } else {
        handlePageChange({
          page: pagination.curPage,
          pageSize: pagination.limitPage,
          filter
        });
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleToggleDisplay = async (
    record: BannerHeader,
    platform: 'PC' | 'MOBILE' | 'APP',
    checked: boolean
  ) => {
    try {
      await dispatch(
        toggleBannerHeaderPlatform({
          id: record.id,
          type: platform,
          status: checked
        })
      ).unwrap();

      message.success(MESSAGE.UPDATE_BANNER_HEADER_SUCCESS);

      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    } catch (error) {
      console.error('Toggle display error:', error);
      message.error(MESSAGE.UPDATE_BANNER_HEADER_FAIL);
    }
  };

  const handleAdd = () => {
    push(`${PageURLs.ofHomeBannerHeader()}?mode=${PermissionMode.CREATE}`);
  };

  const handleEdit = (record: BannerHeader) => {
    push(
      `${PageURLs.ofHomeBannerHeader()}?mode=${PermissionMode.EDIT}&id=${record.id}`
    );
  };

  useEffect(() => {
    if (bannerHeaderMessage) {
      if (bannerHeaderMessage.type === Message.SUCCESS) {
        message.success(bannerHeaderMessage.message);
      } else if (bannerHeaderMessage.type === Message.ERROR) {
        message.error(bannerHeaderMessage.message);
      }
      dispatch(resetMessages());
    }
  }, [bannerHeaderMessage, dispatch]);

  useEffect(() => {
    dispatch(
      getListBannerHeader({
        offset: 1,
        limit: 10
      })
    );
  }, [dispatch]);

  return (
    <div css={rootStyles}>
      <Row css={boxStyles} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Banner Header
          </Typography.Title>
        </Col>
      </Row>

      <div css={boxStyles}>
        <BannerHeaderSearcher onSearch={handleSearch} />
      </div>

      <Row css={boxStyles} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Danh sách Banner Header
          </Typography.Title>
        </Col>
        <Button
          icon={<PlusOutlined />}
          iconPosition='start'
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: '#1890FF',
            color: '#1890FF'
          }}
          onClick={() => handleAdd()}
        >
          Thêm mới Banner Header
        </Button>
      </Row>

      <div css={tableStyles}>
        <BannerHeaderTable
          data={data}
          loading={loading}
          pagination={pagination}
          currentPage={currentPage}
          pageSize={pageSize}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleDisplay={handleToggleDisplay}
          onPageChange={(page, newPageSize) => {
            setCurrentPage(page);
            setPageSize(newPageSize);
            handlePageChange({ page, pageSize: newPageSize, filter });
          }}
        />
      </div>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 0.8rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);

  &:nth-of-type(2) {
    padding-top: 0;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
  font-weight: 700 !important;
`;

const tableStyles = css`
  .ant-spin-container {
    padding: 0.4rem 0.8rem !important;
  }

  background: var(--white-color);
  .ant-table-row .ant-table-cell {
    padding: 4px 8px !important;
  }

  .ant-table-thead .ant-table-cell {
    background-color: var(--blue-dark-color);
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none !important;
  }

  .ant-table-wrapper .ant-table-cell {
    padding: 8px 8px !important;
  }

  .btActionStyle {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ant-table-wrapper
    .ant-table-container
    table
    > thead
    > tr:first-child
    > *:first-child {
    border-start-start-radius: 0px !important;
  }

  .ant-table-wrapper
    .ant-table-container
    table
    > thead
    > tr:first-child
    > *:last-child {
    border-start-end-radius: 0px !important;
  }
`;
